/**
 * Маркер панели выделения на компьютере (T4, 21.10.2026) — `components/ielts/SelectionToolbar.tsx`.
 *
 * 🔴 ПОЧЕМУ НЕ <mark> ВОКРУГ ВЫДЕЛЕНИЯ. Прежний маркер делал `range.extractContents()` и вставлял <mark>. На тексте
 * статьи выделение лежит в одном текстовом узле, и это работало. На вопросах выделение задевает куски блоков
 * (<p>, <label>, ячейки): браузер клонирует обрезанные блоки и переносит их внутрь <mark> — над инструкцией пустые
 * жёлтые полосы, текст съезжает вниз; узлы, которыми владеет React, оказываются не на своих местах.
 *
 * Теперь подсветка — CSS Custom Highlight API: диапазоны регистрируются в `CSS.highlights`, браузер красит их сам
 * (`::highlight(onebilim-hl)` в index.css), DOM не меняется вовсе. Без API (старые браузеры) — <mark> только внутри
 * одного текстового узла: там разрезается один узел и ничего не переезжает; через границы блоков — не подсвечиваем.
 *
 * Живёт как прежняя подсветка статьи: до ухода с секции, нигде не сохраняется.
 */

export const HIGHLIGHT_NAME = 'onebilim-hl';

type HighlightLike = { add(r: AbstractRange): unknown; delete(r: AbstractRange): unknown; forEach(cb: (r: AbstractRange) => void): void };
type Registry = { get(name: string): HighlightLike | undefined; set(name: string, h: HighlightLike): unknown };

function registry(): { reg: Registry; make: (...r: AbstractRange[]) => HighlightLike } | null {
  const css = (globalThis as { CSS?: { highlights?: Registry } }).CSS;
  const H = (globalThis as { Highlight?: new (...r: AbstractRange[]) => HighlightLike }).Highlight;
  if (!css?.highlights || typeof H !== 'function') return null;
  return { reg: css.highlights, make: (...r) => new H(...r) };
}

/**
 * Подсветить выделение. true — подсвечено. DOM меняется только в запасном пути, и только внутри одного текстового
 * узла.
 */
export function highlightRange(range: Range): boolean {
  if (range.collapsed) return false;
  const r = registry();
  if (r) {
    const copy = range.cloneRange();
    const h = r.reg.get(HIGHLIGHT_NAME);
    if (h) h.add(copy);
    else r.reg.set(HIGHLIGHT_NAME, r.make(copy));
    return true;
  }
  if (range.startContainer !== range.endContainer || range.startContainer.nodeType !== Node.TEXT_NODE) return false;
  const mark = document.createElement('mark');
  mark.className = HIGHLIGHT_NAME;
  range.surroundContents(mark);
  return true;
}

/** Снять подсветку внутри контейнера — когда панель уходит вместе с секцией. */
export function clearHighlights(container: Node): void {
  const h = registry()?.reg.get(HIGHLIGHT_NAME);
  if (!h) return;
  const gone: AbstractRange[] = [];
  h.forEach((r) => {
    if (container.contains(r.startContainer)) gone.push(r);
  });
  gone.forEach((r) => h.delete(r));
}

const CONTROL = 'input, textarea, select, button, [role="button"], [contenteditable="true"]';

/** mouseup на поле, кружке, флажке, списке, кнопке — это ответ, а не выделение: панель не нужна. */
export function isAnswerControl(target: EventTarget | null): boolean {
  return target instanceof Element && !!target.closest(CONTROL);
}

/**
 * Клик, которым закончилась протяжка выделения по тексту варианта, — не выбор ответа. Браузер шлёт click по <label>
 * после выделения внутри неё, и вариант отмечался сам. Клик по самому кружку/флажку — выбор, его не трогаем.
 */
export function selectionClickOnLabel(target: EventTarget | null, sel: Selection | null): boolean {
  if (!(target instanceof Element) || isAnswerControl(target)) return false;
  const label = target.closest('label');
  if (!label || !sel || sel.isCollapsed || sel.rangeCount === 0 || !sel.toString().trim()) return false;
  return label.contains(sel.anchorNode) || label.contains(sel.focusNode);
}
